import { COMMA, ENTER } from '@angular/cdk/keycodes';

import {
  Component,
  effect,
  ElementRef,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FilterItemWithLabel } from '@vectronic/util';
import { startWith, Subscription } from 'rxjs';

@Component({
  selector: 'vectronic-chips-filter',
  templateUrl: './chips-filter.component.html',
  styleUrls: ['./chips-filter.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    MatChipsModule,
  ],
})
export class ChipsFilterComponent<T> implements OnDestroy {
  items = input<FilterItemWithLabel<T>[]>([]);
  defaultSelectedItems = input<FilterItemWithLabel<T>[]>([]);
  allowSelectionMultipleTimes = input(false);
  outlined = input(false);
  label = input('');
  disabled = input(false);

  selectItem = output<FilterItemWithLabel<T>>();
  deleteItem = output<{ item: FilterItemWithLabel<T>; index: number }>();

  readonly selectedItems = signal<FilterItemWithLabel<T>[]>([]);
  readonly filteredItems = signal<FilterItemWithLabel<T>[]>([]);
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly ctrl = new FormControl('');

  @ViewChild('itemInput') itemInput: ElementRef<HTMLInputElement>;
  @ViewChild('itemInput', { read: MatAutocompleteTrigger })
  autocompleteTrigger: MatAutocompleteTrigger;

  private subscription: Subscription = this.ctrl.valueChanges
    .pipe(startWith(null))
    .subscribe((value) => this.updateFilteredItems(value));

  // noinspection JSUnusedGlobalSymbols
  private syncDefaultSelectedItems = effect(() => {
    this.selectedItems.set(this.defaultSelectedItems() ?? []);
  });

  // noinspection JSUnusedGlobalSymbols
  private syncFilteredItems = effect(() => {
    this.updateFilteredItems(this.ctrl.value);
  });

  remove(item: FilterItemWithLabel<T>, index: number) {
    if (this.disabled()) return;

    this.updatePanelWidth();
    this.deleteItem.emit({ item, index });
    this.selectedItems.update((preSelectedItems) => preSelectedItems.toSpliced(index, 1));
    this.updateFilteredItems(this.ctrl.value);
  }

  selected(event: MatAutocompleteSelectedEvent) {
    if (this.disabled()) return;

    const item = this.items().find((i) => i.item === event.option.value);
    if (!item) return;

    this.selectItem.emit(item);
    this.selectedItems.update((preSelectedItems) => {
      const newSelectedItems = preSelectedItems ? [...preSelectedItems] : [];
      newSelectedItems.push(item);
      return newSelectedItems;
    });
    this.itemInput.nativeElement.value = '';
    this.ctrl.setValue(undefined);
    this.updateFilteredItems(this.ctrl.value);
  }

  focusInput() {
    if (this.disabled()) return;

    this.updatePanelWidth();
    setTimeout(() => this.autocompleteTrigger?.openPanel());
  }

  currentPanelWidth: string | number = 'auto';

  updatePanelWidth() {
    const width = this.itemInput.nativeElement.closest('.chips-filter')?.clientWidth;
    if (width) {
      this.currentPanelWidth = width + 'px';
      this.autocompleteTrigger.autocomplete.panelWidth = this.currentPanelWidth;
    }
  }

  private filterItems(
    value: string | null | undefined,
    items: FilterItemWithLabel<T>[],
    selectedItems: FilterItemWithLabel<T>[],
    allowSelectionMultipleTimes: boolean,
  ): FilterItemWithLabel<T>[] {
    if (typeof value !== 'string' || !value)
      return items
        ?.filter(
          (i) => allowSelectionMultipleTimes || !selectedItems?.some((it) => it.label === i.label),
        )
        .slice(0, 2000);

    const filterValue = value.toLowerCase();
    return items
      .filter(
        (i) =>
          (i.label?.toLowerCase().includes(filterValue) ||
            i.additionalInfo?.toLowerCase().includes(filterValue)) &&
          (allowSelectionMultipleTimes || !selectedItems?.some((it) => it.label === i.label)),
      )
      .slice(0, 2000);
  }

  private updateFilteredItems(value: string | null | undefined) {
    this.filteredItems.set(
      this.filterItems(
        value,
        this.items(),
        this.selectedItems(),
        this.allowSelectionMultipleTimes(),
      ),
    );
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}
